'use client';
/**
 * @fileoverview Email change request form (US-12.14).
 * @purpose Allow authenticated users to initiate an email address change via confirmation link.
 */

import { useState } from 'react';

type EmailChangeFormProps = {
  currentEmail: string;
};

export function EmailChangeForm({ currentEmail }: EmailChangeFormProps) {
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || trimmed === currentEmail.trim().toLowerCase()) {
      setMessage({ type: 'error', text: 'New email must differ from the current email.' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/user/email-change/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail: trimmed }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Confirmation link sent to ${trimmed}. Check your inbox.` });
        setNewEmail('');
      } else {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        setMessage({ type: 'error', text: data.message ?? 'Failed to send confirmation email.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-3">
      <div>
        <label htmlFor="newEmail" className="block text-sm font-medium text-foreground-default">
          New Email Address
        </label>
        <div className="mt-1">
          <input
            id="newEmail"
            name="newEmail"
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary-default focus:border-primary-default sm:text-sm bg-background-secondary text-foreground-default"
            placeholder="new@example.com"
          />
        </div>
      </div>

      {message && (
        <p className={`text-sm ${message.type === 'success' ? 'text-success-default' : 'text-danger-default'}`}>
          {message.text}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading || !newEmail.trim()}
        className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Sending…' : 'Send Confirmation'}
      </button>
    </form>
  );
}
